import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Loader2, ClipboardPaste, Lock, Calculator } from "lucide-react";
import { money } from "@/components/shared";
import { compsStats } from "@/components/ResaleSection";

const rid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Math.random()).slice(2));
const emptyRow = () => ({
  id: rid(), address: "", building: "", condition: "", beds: "", baths: "", parking: "",
  sqft: "", sold_price: "", list_price: "", dom: "", closed_date: "", notes: "",
});

function RowEditor({ row, kind, idx = 0, onChange, onDelete }) {
  const f = (k) => (e) => onChange({ ...row, [k]: e.target.value });
  const fields = [
    ["address", "Address"], ["building", "Building / complex"], ["condition", "Condition"],
    ["beds", "Beds"], ["baths", "Baths"], ["parking", "Parking"], ["sqft", "Sq ft"],
    ...(kind === "sold" ? [["sold_price", "Sold $"]] : []),
    ["list_price", "List $"], ["dom", "Days on mkt"],
    ...(kind === "sold" ? [["closed_date", "Closed date"]] : []),
  ];
  return (
    <div className="p-3 rounded-xl border border-border relative bg-card" data-testid={`comp-row-${kind}-${idx}`}>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pr-8">
        {fields.map(([k, label]) => (
          <div key={k} className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
            <Input value={row[k] ?? ""} onChange={f(k)} className="h-8 text-sm" data-testid={`comp-${kind}-${k}`} />
          </div>
        ))}
      </div>
      <div className="mt-2 space-y-1">
        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Notes</Label>
        <Input value={row.notes ?? ""} onChange={f("notes")} className="h-8 text-sm" data-testid={`comp-${kind}-notes`} />
      </div>
      <Button variant="ghost" size="icon" className="absolute top-2 right-2 h-7 w-7 text-destructive" onClick={onDelete} data-testid={`comp-${kind}-delete`}>
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export function ResaleEditor({ tourId, resale, onSaved }) {
  const blank = { sold: [], for_sale: [], summary: "", listing_history: "", est_resale_range: "", agent_notes: "" };
  const [state, setState] = useState(() => ({ ...blank, ...(resale || {}) }));
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");

  const setField = (k) => (e) => setState((s) => ({ ...s, [k]: e.target.value }));
  const updateRow = (arr, idx) => (row) => setState((s) => { const a = [...s[arr]]; a[idx] = row; return { ...s, [arr]: a }; });
  const addRow = (arr) => () => setState((s) => ({ ...s, [arr]: [...s[arr], emptyRow()] }));
  const delRow = (arr, idx) => () => setState((s) => ({ ...s, [arr]: s[arr].filter((_, i) => i !== idx) }));

  const toPayload = () => {
    const toInt = (v) => (v === "" || v == null ? null : Number(v));
    const clean = (rows) => rows.map((r) => ({ ...r, sold_price: toInt(r.sold_price), list_price: toInt(r.list_price) }));
    return { ...state, sold: clean(state.sold), for_sale: clean(state.for_sale) };
  };

  const save = useMutation({
    mutationFn: async () => (await api.put(`/agent/tours/${tourId}/resale`, toPayload())).data,
    onSuccess: () => { toast.success("Comps saved"); onSaved && onSaved(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const parse = useMutation({
    mutationFn: async () => (await api.post(`/agent/tours/${tourId}/parse-comps`, { text: pasteText })).data,
    onSuccess: (data) => {
      setState((s) => ({ ...s, sold: [...s.sold, ...(data.sold || [])], for_sale: [...s.for_sale, ...(data.for_sale || [])] }));
      setPasteOpen(false); setPasteText("");
      toast.success("Rows added below — review and edit, then Save");
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const stats = compsStats(toPayload().sold);

  return (
    <div className="space-y-4" data-testid="resale-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">Comps, competition and resale notes for this home. Buyers see sold comps, summary, for-sale and the estimate range — never your agent-only notes.</p>
        <div className="flex gap-2">
          <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
            <Button variant="outline" size="sm" className="rounded-full gap-1 h-8" onClick={() => setPasteOpen(true)} data-testid="paste-comps-btn">
              <ClipboardPaste className="h-3.5 w-3.5" /> Paste comps
            </Button>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">Paste comps</DialogTitle>
                <DialogDescription>Paste a block of text or a table of sold / for-sale listings. We pre-fill rows for you to review — nothing is invented, blanks stay blank. Review and edit before saving.</DialogDescription>
              </DialogHeader>
              <Textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} rows={10} className="rounded-xl font-mono text-xs" placeholder="e.g. 98-1456 Koaheahe St #72 — Kai D — Excellent — 1.5/1 — sold $630,000 — 7/15/26 ..." data-testid="paste-comps-text" />
              <DialogFooter>
                <Button onClick={() => parse.mutate()} disabled={!pasteText.trim() || parse.isPending} className="rounded-full" data-testid="paste-comps-parse">
                  {parse.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pre-fill rows"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button size="sm" className="rounded-full h-8" onClick={() => save.mutate()} disabled={save.isPending} data-testid="resale-save-btn">
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save comps"}
          </Button>
        </div>
      </div>

      {stats.count > 0 && (
        <div className="p-3 rounded-xl bg-forest/5 border border-forest/20 text-xs flex flex-wrap items-center gap-x-5 gap-y-1" data-testid="editor-stats">
          <span className="inline-flex items-center gap-1 font-semibold text-forest"><Calculator className="h-3.5 w-3.5" /> Sold comps ({stats.count})</span>
          <span>Average: <span className="font-semibold">{money(stats.avg)}</span></span>
          <span>Median: <span className="font-semibold">{money(stats.median)}</span></span>
          {stats.ppsf ? <span>Avg price/sq ft: <span className="font-semibold">${stats.ppsf.toLocaleString()}</span></span> : <span className="text-muted-foreground">Price/sq ft: add sq ft to calculate</span>}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Sold comps</p>
          <Button variant="outline" size="sm" className="rounded-full h-7 gap-1" onClick={addRow("sold")} data-testid="add-sold-comp"><Plus className="h-3 w-3" /> Add sold comp</Button>
        </div>
        <div className="space-y-2">
          {state.sold.length === 0 && <p className="text-xs text-muted-foreground">No sold comps yet.</p>}
          {state.sold.map((row, i) => <RowEditor key={row.id || i} row={row} kind="sold" idx={i} onChange={updateRow("sold", i)} onDelete={delRow("sold", i)} />)}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Currently for sale (competition)</p>
          <Button variant="outline" size="sm" className="rounded-full h-7 gap-1" onClick={addRow("for_sale")} data-testid="add-forsale-comp"><Plus className="h-3 w-3" /> Add listing</Button>
        </div>
        <div className="space-y-2">
          {state.for_sale.length === 0 && <p className="text-xs text-muted-foreground">No active listings yet.</p>}
          {state.for_sale.map((row, i) => <RowEditor key={row.id || i} row={row} kind="for_sale" idx={i} onChange={updateRow("for_sale", i)} onDelete={delRow("for_sale", i)} />)}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Estimated resale range <span className="text-muted-foreground font-normal">(buyer sees this — leave blank to hide)</span></Label>
        <Input value={state.est_resale_range} onChange={setField("est_resale_range")} placeholder="e.g. $590K – $620K" className="rounded-xl" data-testid="resale-range-input" />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Summary <span className="text-muted-foreground font-normal">(buyer sees this)</span></Label>
        <Textarea value={state.summary} onChange={setField("summary")} className="rounded-xl" rows={3} data-testid="resale-summary-input" />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Listing history <span className="text-muted-foreground font-normal">(buyer sees this)</span></Label>
        <Textarea value={state.listing_history} onChange={setField("listing_history")} className="rounded-xl" rows={2} data-testid="resale-history-input" />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs flex items-center gap-1 text-primary"><Lock className="h-3 w-3" /> Agent-only notes <span className="text-muted-foreground font-normal">(never shown to buyers)</span></Label>
        <Textarea value={state.agent_notes} onChange={setField("agent_notes")} className="rounded-xl" rows={4} data-testid="resale-agent-notes-input" />
      </div>

      <Button size="sm" className="rounded-full h-8" onClick={() => save.mutate()} disabled={save.isPending} data-testid="resale-save-btn-bottom">
        {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save comps"}
      </Button>
    </div>
  );
}
